import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const emojis = ["🍎", "🏀", "🐠", "🌟", "🍕", "🚗", "🐶", "🎈", "🦋"];

const CircleLargerOrSmallerObject: React.FC = () => {
    const [questionType, setQuestionType] = useState<"larger" | "smaller">("larger");
    const [question, setQuestion] = useState<{ object: string; leftCount: number; rightCount: number } | null>(null);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Bigger or Smaller – Nursery Maths Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const generateNewQuestion = () => {
        const object = emojis[Math.floor(Math.random() * emojis.length)];
        const leftCount = Math.floor(Math.random() * 9) + 1;  // Random count (1-9)
        let rightCount;

        // Ensure the counts are different
        do {
            rightCount = Math.floor(Math.random() * 9) + 1;
        } while (rightCount === leftCount);

        // Randomly choose between "larger" or "smaller"
        const newQuestionType = Math.random() > 0.5 ? "larger" : "smaller";

        setQuestionType(newQuestionType);
        setQuestion({ object, leftCount, rightCount });
        setMessage("");
    };

    const handleSelection = (side: "left" | "right") => {
        if (!question) return;

        const isLarger = question.leftCount > question.rightCount ? "left" : "right";
        const isSmaller = question.leftCount < question.rightCount ? "left" : "right";

        const isCorrect =
            (questionType === "larger" && side === isLarger) ||
            (questionType === "smaller" && side === isSmaller);

        if (isCorrect) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion();
                setShowImage(false);
                setMessage('')
            }, 5000); // Delay reduced to 3 seconds
        } else {
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help nursery kids understand size comparisons with the "Bigger or Smaller" activity on Mamas Guidance—an interactive exercise designed to teach children how to identify and compare objects based on size.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart additionWord matchImage letterOption match-container" style={{ textAlign: "center", padding: "20px" }}>
                        <div className="showInfo" onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Bigger or Smaller</h1>

                                <p>
                                    Understanding the concepts of <strong>bigger</strong> and <strong>smaller</strong> is an important milestone in early math learning. This worksheet from <strong>Mama’s Guidance</strong> helps your child compare numbers and identify which one is greater or less — in a simple and engaging way!
                                </p>

                                <h2>🔍 What’s Inside This Worksheet?</h2>
                                <p>
                                    Children will see two numbers or groups of objects side by side. They are asked to circle, tick, or point out which is <strong>bigger</strong> or <strong>smaller</strong>. This helps reinforce size comparison and numerical value recognition.
                                </p>

                                <h2>🎯 Learning Outcomes</h2>
                                <ul>
                                    <li>Compare numbers between 1 and 20</li>
                                    <li>Understand <strong>greater than / less than</strong> concepts</li>
                                    <li>Develop early <strong>logical reasoning</strong> and <strong>critical thinking</strong></li>
                                    <li>Improve attention to detail and number sense</li>
                                </ul>

                                <h2>👩‍🏫 How to Use This Worksheet</h2>
                                <ul>
                                    <li>Ask your child to look at each number pair.</li>
                                    <li>Have them say which number is <strong>bigger or smaller</strong>.</li>
                                    <li>Let them mark their answer using crayons or pencils.</li>
                                    <li>Discuss <strong>real-life examples</strong> using toys, snacks, or blocks!</li>
                                </ul>

                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/mathematics/count-object"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Count Object</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/mathematics/before-after-between-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Numbers</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why Comparison Matters in Early Math</h2>
                                <p>
                                    Learning to compare values lays the groundwork for understanding <strong>number order</strong>, <strong>measurement</strong>, and <strong>arithmetic operations</strong>. This worksheet helps children see how numbers relate to each other — which is a key skill for kindergarten and beyond.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Bigger or Smaller</h1>

                            <p>
                                Understanding the concepts of <strong>bigger</strong> and <strong>smaller</strong> is an important milestone in early math learning. This worksheet from <strong>Mama’s Guidance</strong> helps your child compare numbers and identify which one is greater or less — in a simple and engaging way!
                            </p>

                            <h2>🔍 What’s Inside This Worksheet?</h2>
                            <p>
                                Children will see two numbers or groups of objects side by side. They are asked to circle, tick, or point out which is <strong>bigger</strong> or <strong>smaller</strong>. This helps reinforce size comparison and numerical value recognition.
                            </p>

                            <h2>🎯 Learning Outcomes</h2>
                            <ul>
                                <li>Compare numbers between 1 and 20</li>
                                <li>Understand greater than / less than concepts</li>
                                <li>Develop early logical reasoning and critical thinking</li>
                                <li>Improve attention to detail and number sense</li>
                            </ul>

                            <h2>👩‍🏫 How to Use This Worksheet</h2>
                            <ol>
                                <li>Ask your child to look at each number pair.</li>
                                <li>Have them say which number is bigger or smaller.</li>
                                <li>Let them mark their answer using crayons or pencils.</li>
                                <li>Discuss real-life examples using toys, snacks, or blocks!</li>
                            </ol>

                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/mathematics/count-object"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Count Object</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/missing-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Missing Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/before-after-between-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Before / After / Between Numbers</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why Comparison Matters in Early Math</h2>
                            <p>
                                Learning to compare values lays the groundwork for understanding number order, measurement, and arithmetic operations. This worksheet helps children see how numbers relate to each other — which is a key skill for kindergarten and beyond.
                            </p>

                        </article>
                        <h3>Choose the {questionType === "larger" ? "larger" : "smaller"} group</h3>
                        {question && (
                            <div className="row" style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                                {/* Left Side */}
                                <div className="col"
                                    onClick={() => handleSelection("left")}
                                    style={{ cursor: "pointer", padding: "10px", borderRight: "2px solid black", width: "50%" }}
                                >
                                    <p style={{ fontSize: "30px" }}>{question.object.repeat(question.leftCount)}</p>
                                </div>

                                {/* Right Side */}
                                <div className="col"
                                    onClick={() => handleSelection("right")}
                                    style={{ cursor: "pointer", padding: "10px", borderRadius: "10px" }}
                                >
                                    <p style={{ fontSize: "30px" }}>{question.object.repeat(question.rightCount)}</p>
                                </div>
                            </div>
                        )}
                        {message && <h4 style={{ marginTop: "20px", color: message.includes("Correct") ? "green" : "red" }}>{message}</h4>}
                        <div className="btn hero-btn" onClick={generateNewQuestion} style={{ marginTop: "20px" }}>Next &gt;&gt;</div>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default CircleLargerOrSmallerObject;
